import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";

/**
 * Research Committee page component
 * @returns {JSX.Element}
 * @component
 */
const ResearchCommittee = () => {
  // Extract state data with safe access patterns
  const { committees: committeesData } =
    useSelector((state) => state.pageContents) || {};

  // Get loading state from Redux
  const isLoading = useSelector(
    (state) => state.pageContents?.loading || !state.pageContents?.committees,
  );

  // Carousel responsive configuration
  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
      slidesToSlide: 4,
    },
    tablet: {
      breakpoint: { max: 1024, min: 768 },
      items: 3,
      slidesToSlide: 3,
    },
    mobile: {
      breakpoint: { max: 767, min: 464 },
      items: 2,
      slidesToSlide: 1,
    },
  };

  return (
    <PageLayout
      pageName="Research Committee"
      showLoader={isLoading}
      checkComingSoon
    >
      {/* Banner */}
      {committeesData?.researchbanner ? (
        <PageBanner
          bannerImage={committeesData.researchbanner.banner_image}
          mainTitle={
            committeesData.researchbanner.banner_title || "Research Committee"
          }
          pageTitle="Committees"
        />
      ) : (
        <PageBanner mainTitle="Research Committee" pageTitle="Committees" />
      )}

      {/* Committee Content Section */}
      <Box component="section" sx={{ py: 6 }}>
        <Container>
          {/* Committee Header */}
          {committeesData?.researchmembership?.section_title && (
            <Typography
              variant="h4"
              component="h1"
              align="center"
              gutterBottom
              sx={{ mb: 4 }}
              className="committe-head"
            >
              {committeesData.researchmembership.section_title}
            </Typography>
          )}

          {/* Committee Details */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Committee Image */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Box
                component="img"
                src={committeesData?.researchmembership?.section_image}
                alt="Committee"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  boxShadow: 3,
                }}
                className="commitee-img"
              />
            </Grid>

            {/* Committee Info */}
            <Grid size={{ xs: 12, md: 9 }}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  height: "100%",
                }}
                className="doctors-detailss committes-detailss"
              >
                {/* Chair */}
                <Grid container sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      className="left-title"
                    >
                      Chair
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 9 }}>
                    <List disablePadding className="right-title">
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ChevronRightIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={committeesData?.researchmembership?.chair}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    width: "100%",
                    height: "1px",
                    bgcolor: "divider",
                    my: 2,
                  }}
                />

                {/* Vice Chair */}
                <Grid container sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      className="left-title"
                    >
                      Vice Chair
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 9 }}>
                    <List disablePadding className="right-title">
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ChevronRightIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            committeesData?.researchmembership?.vice_chair
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    width: "100%",
                    height: "1px",
                    bgcolor: "divider",
                    my: 2,
                  }}
                />

                {/* Newsletter Editor */}
                <Grid container>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      className="left-title"
                    >
                      Newsletter Editor
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 9 }}>
                    <List disablePadding className="right-title">
                      <ListItem disableGutters>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ChevronRightIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            committeesData?.researchmembership
                              ?.news_leeter_editor
                          }
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Committee Description */}
          <Paper
            elevation={1}
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 6,
            }}
            className="committee-sec-inner"
          >
            {committeesData?.researchmembership?.description && (
              <Box
                dangerouslySetInnerHTML={{
                  __html: committeesData.researchmembership.description,
                }}
                sx={{
                  "& p": { mb: 2, lineHeight: 1.7 },
                  "& ul, & ol": {
                    mb: 2,
                    pl: 3,
                    "& li": { mb: 1 },
                  },
                }}
              />
            )}
          </Paper>

          {/* Committee Members Carousel */}
          {committeesData?.CommitiMember?.length > 0 && (
            <Box
              sx={{
                bgcolor: "#f9fbfc",
                py: 5,
                px: 2,
                borderRadius: 2,
              }}
              className="Committee-members-sec"
            >
              <Typography
                variant="h4"
                component="h2"
                align="center"
                gutterBottom
                sx={{ mb: 4 }}
              >
                Committee Members
              </Typography>

              <Carousel
                responsive={responsive}
                autoPlay={false}
                swipeable={true}
                draggable={true}
                showDots={true}
                infinite={true}
                partialVisible={false}
                dotListClassName="custom-dot-list-style"
              >
                {committeesData.CommitiMember.map((item, index) => (
                  <Card
                    key={index}
                    elevation={2}
                    sx={{
                      m: 1,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "translateY(-5px)",
                      },
                    }}
                    className="slider"
                  >
                    <CardContent
                      sx={{ flexGrow: 1, textAlign: "center" }}
                      className="slider-sec"
                    >
                      <Typography variant="h6" gutterBottom>
                        {item.section_title} {item.section_qualification}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.section_des}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Carousel>
            </Box>
          )}
        </Container>
      </Box>
    </PageLayout>
  );
};

export default ResearchCommittee;
